import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import * as amplitude from '@amplitude/analytics-browser';
import { useTranslation } from 'react-i18next';

// Types for configuration
// `value` is the English answer that is tracked; `text` is what is shown.
export interface LoadingAnswer {
  value: string;
  text: string;
}

export interface LoadingQuestion {
  id: string;
  text: string;
  answers: LoadingAnswer[];
}

export interface LoadingItem {
  id: string;
  title: string;
  text: string;
  duration: number;
  question?: LoadingQuestion;
}

export interface LoadingPageConfig {
  items: LoadingItem[];
  onComplete?: () => void;
  onQuestionAnswer?: (questionId: string, answer: string) => void;
  isFadingOut?: boolean;
}

// Styled components
const LoadingContainer = styled.div<{ isFadingOut?: boolean }>`
  width: 100%;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: 'Lato', sans-serif;
  opacity: ${props => props.isFadingOut ? 0 : 1};
  transition: opacity 0.5s ease;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 16px;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 72px);
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #F8F8F8;
`;

const Content = styled.div`
  width: 100%;
  max-width: 640px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 24px;
  box-sizing: border-box;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #161616;
  text-align: center;
  margin-top: 24px;
`;

const ProgressCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  margin-top: 24px;
  box-shadow: 0px 1px 3px 0px #0000000D;
  padding: 24px;
  box-sizing: border-box;
  width: 100%;
`;

const ProgressItem = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: 32px;
  opacity: ${props => props.isActive || props.isCompleted ? 1 : 0.6};
  transition: opacity 0.3s ease;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const ProgressIcon = styled.div<{ isCompleted: boolean; isActive: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  
  background: ${props => 
    props.isCompleted ? '#0B6DFF' : 
    props.isActive ? '#0B6DFF' : '#E5E5E5'};
  color: ${props => 
    props.isCompleted ? 'white' : 
    props.isActive ? 'white' : '#999'};
`;

const ProgressContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProgressText = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ##161616;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ProgressPercentage = styled.div<{ isActive: boolean }>`
  font-size: 13px;
  font-weight: 500;
  color: #406EDF;
  margin-left: auto;
`;

const ProgressBarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 6px;
  background: #E5E7EB;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 6px;
`;

const ProgressBar = styled.div<{ progress: number; isActive: boolean }>`
  height: 100%;
  background: ${props => props.isActive || props.progress === 100 ? '#0B6DFF' : '#E5E7EB'};
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
  border-radius: 4px;
`;

const ProgressDescription = styled.div`
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
  color: #909090;
  margin-top: 6px;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 24px;
`;

const Badges = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const Badge = styled.div`
  font-family: 'Oswald';
  background: linear-gradient(270deg, #E7F0FF 3.83%, #F8F8F8 100%);
  color: #0B6DFF;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
`;

const FooterText = styled.p`
  font-size: 13px;
  color: #909090;
  line-height: 18px;
  font-weight: 500;

`;

// Question Modal Components
const QuestionOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #00000014;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
`;

const QuestionCard = styled.div`
  background: white;
  border-radius: 16px 16px 0 0;
  padding: 24px 24px 64px;
  max-width: 400px;
  width: 100%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const QuestionIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #FF9500, #FF6B00);
  border-radius: 12px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  
  &::before {
    content: '🌅';
  }
`;

const QuestionText = styled.h3`
  font-size: 17px;
  line-height: 24px;
  font-weight: 700;
  color: #161616;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const AnswerButton = styled.button`
  font-family: 'Arial';
  width: 100%;
  padding: 16px;
  margin-bottom: 12px;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  background: white;
  color: #1F2937;
  font-size: 15px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    border-color: #0B6DFF;
    background: #F8F9FF;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export function LoadingPageV2({ items, onComplete, onQuestionAnswer, isFadingOut }: LoadingPageConfig) {
  const { t } = useTranslation();
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [itemProgress, setItemProgress] = useState<Record<number, number>>({});
  const [showQuestion, setShowQuestion] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState<LoadingQuestion | null>(null);
  const [startTime, setStartTime] = useState<number>(Date.now());

  useEffect(() => {
    amplitude.track("LoadingPageV2Viewed");
  }, []);

  useEffect(() => {
    if (currentItemIndex >= items.length) {
      amplitude.track("LoadingPageV2Completed");
      setTimeout(() => {
        onComplete?.();
      }, 1000);
      return;
    }

    const currentItem = items[currentItemIndex];
    
    // Initialize progress for current item
    setItemProgress(prev => ({ ...prev, [currentItemIndex]: 0 }));

    // Animate progress from 0 to 100
    let animationId: number;

    // Different easing functions for variety - all accelerate towards the end (gentler)
    const easingFunctions = [
      // Ease in quad (gentle acceleration)
      (t: number) => t * t,
      // Ease in expo (gentle exponential acceleration)
      (t: number) => t === 0 ? 0 : Math.pow(2, 8 * (t - 1)),
      // Ease in out cubic (accelerate, decelerate middle, accelerate end)
      (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
      // Ease in out quart (accelerate, decelerate middle, accelerate end)
      (t: number) => t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2,
    ];

    // Select easing function based on item index
    const selectedEasing = easingFunctions[currentItemIndex % easingFunctions.length];

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const normalizedTime = Math.min(elapsed / currentItem.duration, 1);
      const easedTime = selectedEasing(normalizedTime);
      
      // Clamp the eased time to ensure it's between 0 and 1
      const clampedEasedTime = Math.max(0, Math.min(1, easedTime));
      const newProgress = Math.min(clampedEasedTime * 100, 100);

      setItemProgress(prev => ({ ...prev, [currentItemIndex]: newProgress }));

      if (normalizedTime < 1 || newProgress < 100) {
        animationId = requestAnimationFrame(animate);
      } else {
        // Animation complete - set final progress and stop
        setItemProgress(prev => ({ ...prev, [currentItemIndex]: 100 }));
        // Small delay to ensure progress is set, then show question or move to next
        //setTimeout(() => {
          if (currentItem.question) {
            setCurrentQuestion(currentItem.question);
            setShowQuestion(true);
          } else {
            // Move to next item
            setTimeout(() => {
              setCurrentItemIndex(prev => prev + 1);
              setStartTime(Date.now());
            }, 10);
          }
        //}, 10);
      }
    };

    animationId = requestAnimationFrame(animate);

    // Cleanup function to cancel animation if component unmounts or currentItemIndex changes
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [currentItemIndex, startTime, items, onComplete]);

  useEffect(() => {
    if (showQuestion && currentQuestion) {
      document.body.classList.add('hide-overflow');
    } else {
      document.body.classList.remove('hide-overflow');
    }
  }, [showQuestion && currentQuestion]);

  const handleQuestionAnswer = (answer: string) => {
    if (currentQuestion) {
      amplitude.track("LoadingPageV2QuestionAnswered", {
        questionId: currentQuestion?.id,
        answer: answer,
      });

      onQuestionAnswer?.(currentQuestion.id, answer);
    }
    
    setShowQuestion(false);
    setCurrentQuestion(null);
    
    // Move to next item after a short delay
    setTimeout(() => {
      setCurrentItemIndex(prev => prev + 1);
      setStartTime(Date.now());
    }, 300);
  };

  const getItemStatus = (index: number) => {
    if (index < currentItemIndex) return 'completed';
    if (index === currentItemIndex) return 'active';
    return 'pending';
  };

  return (
    <>
      <LoadingContainer isFadingOut={isFadingOut}>
        <Header>
          <img src="/logo2.svg" alt="" />
        </Header>
        <ContentWrapper>
          <Content>
            <Title>{t("results.loading.title")}</Title>

            <ProgressCard>
              {items.map((item, index) => {
                const status = getItemStatus(index);
                const isCompleted = status === 'completed';
                const isActive = status === 'active';
                const itemProgressValue = Math.round(isCompleted ? 100 : isActive ? (itemProgress[index] || 0) : 0);

                return (
                  <ProgressItem key={item.id} isActive={isActive} isCompleted={isCompleted}>
                    
                    <ProgressContent>
                      <ProgressBarHeader>
                        <ProgressIcon isCompleted={isCompleted} isActive={isActive}>
                          {isCompleted ? (
                            <svg width="10" height="9" viewBox="0 0 10 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9 1L3.90286 8L1 4.76923" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>                      
                          ) : isActive ? (index + 1) : (index + 1)}
                        </ProgressIcon>
                        <ProgressText>{item.title}</ProgressText>
                        <ProgressPercentage isActive={isActive}>
                          {Math.round(itemProgressValue)}%
                        </ProgressPercentage>
                      </ProgressBarHeader>
                      
                      <ProgressBarContainer>
                        <ProgressBar progress={itemProgressValue} isActive={isActive} />
                      </ProgressBarContainer>
                      
                      {itemProgressValue > 0 && (
                        <ProgressDescription>
                            {item.text}
                        </ProgressDescription>
                      )}
                    </ProgressContent>
                  </ProgressItem>
                );
              })}
            </ProgressCard>

            <Footer>
              <Badges>
                <Badge>{t("results.loading.badgeResearch")}</Badge>
                <Badge>{t("results.loading.badgeMembers")}</Badge>
              </Badges>
              <FooterText>
                {t("results.loading.footer")}
              </FooterText>
            </Footer>
          </Content>
        </ContentWrapper>
      </LoadingContainer>

      {showQuestion && currentQuestion && (
        <QuestionOverlay>
          <QuestionCard>
            <QuestionIcon />
            <QuestionText>{currentQuestion.text}</QuestionText>
            {currentQuestion.answers.map((answer, index) => (
              <AnswerButton
                key={index}
                onClick={() => handleQuestionAnswer(answer.value)}
              >
                {answer.text}
              </AnswerButton>
            ))}
          </QuestionCard>
        </QuestionOverlay>
      )}
    </>
  );
}